import { createSelector } from 'reselect';
import cartUtils from '../utils/cart';
import { imageIntentGalleryFromAdditionalData } from '../components/v3/shared/protectedImage/imageIntentGallery';

const EMPTY_DATA = {};
const EMPTY_SHOOT = { backgroundOptions: [], crop_options: [] };

// The Pics gallery, or else the DP2 gallery, each additionalData was built
// for (the same identity save-intent reports use).
const sameGalleryIdentity = (
  orderData: Parameters<typeof imageIntentGalleryFromAdditionalData>[0],
  loadedData: Parameters<typeof imageIntentGalleryFromAdditionalData>[0],
): boolean => {
  const order = imageIntentGalleryFromAdditionalData(orderData);
  const loaded = imageIntentGalleryFromAdditionalData(loadedData);
  return (
    String(order.picsGalleryId ?? '') === String(loaded.picsGalleryId ?? '') &&
    String(order.dp2GalleryId ?? '') === String(loaded.dp2GalleryId ?? '')
  );
};

// The order key is the only configuration context stored in Redux. React
// descendants and action thunks resolve it here, against the same cart snapshot.
// Passing null explicitly retains ordinary global-context add-to-cart flows.
export const selectConfigurationContext = createSelector(
  (
    state: any,
    orderKey: string | null = state.productConfig?.configurationOrderKey ??
      null,
  ) => orderKey,
  (state: any) => state.v3Order?.orders,
  (state: any) => state.shoot,
  (state: any) => state.additionalData,
  (orderKey, orders, globalShoot, globalData) => {
    if (orderKey === null) {
      const eventId = globalShoot?.shoot?.event_id || globalShoot?.shoot?.id;
      const key = `${eventId}-${globalData?.selectedStudent?.id}`;
      return {
        available: true,
        orderKey: key,
        order: orders?.[key],
        shoot: globalShoot,
        additionalData: globalData,
      };
    }

    const order = orders?.[orderKey];
    const data = order?.additionalData;
    const eventId = order?.event?.id;
    const studentId = order?.student?.id;
    const sameEvent =
      eventId != null &&
      String(eventId) ===
        String(globalShoot?.shoot?.event_id || globalShoot?.shoot?.id);
    // Galleries in one event can carry different price lists and options, so
    // the loaded catalog stands in for the order's only when it was loaded
    // for the same gallery too.
    const sameGallery = sameGalleryIdentity(data, globalData);
    const snapshot = order?.productContext?.configurationShoot;
    const completeSnapshot =
      snapshot &&
      typeof snapshot.isGreenScreen === 'boolean' &&
      typeof snapshot.crop_options_enabled === 'boolean' &&
      Array.isArray(snapshot.crop_options) &&
      Array.isArray(snapshot.backgroundOptions);
    const useLoadedContext = sameEvent && sameGallery;
    const eventContext = useLoadedContext ? globalShoot : snapshot;
    const hasImageContext =
      order?.isPrepay === true ||
      Boolean(
        data?.isGeskus && !data?.picsGalleryId && data?.studentPreviewImageUrl,
      ) ||
      [
        'availablePoses',
        'studentGallery',
        'allAvailablePosesForOrder',
        'studentGroupImages',
      ].some((field) => Array.isArray(data?.[field]));
    const available = Boolean(
      order &&
        data &&
        hasImageContext &&
        studentId != null &&
        eventId != null &&
        orderKey === `${eventId}-${studentId}` &&
        (data.selectedStudent?.id == null ||
          String(data.selectedStudent.id) === String(studentId)) &&
        typeof order.isPrepay === 'boolean' &&
        eventContext &&
        (useLoadedContext ||
          (completeSnapshot &&
            String(snapshot?.shoot?.event_id || snapshot?.shoot?.id) ===
              String(eventId))),
    );
    if (!available)
      return {
        available: false,
        orderKey,
        order,
        shoot: EMPTY_SHOOT,
        additionalData: EMPTY_DATA,
      };

    return {
      available: true,
      orderKey,
      order,
      additionalData: {
        ...data,
        selectedStudent: data.selectedStudent || order.student,
      },
      shoot: {
        ...eventContext,
        shoot: { ...eventContext.shoot, ...order.event, event_id: eventId },
        isPrepay: order.isPrepay,
        backgroundOptions:
          order.productContext?.backgroundOptions ??
          eventContext.backgroundOptions ??
          [],
        incentiveProducts:
          order.productContext?.incentiveProducts ??
          eventContext.incentiveProducts ??
          [],
        retouchingProducts:
          order.productContext?.retouchingProducts ??
          eventContext.retouchingProducts ??
          [],
      },
    };
  },
);

export const selectConfigurationShoot = createSelector(
  selectConfigurationContext,
  (context) => context.shoot,
);
export const selectConfigurationReady = createSelector(
  (state: any) => state.productConfig?.currentProduct,
  (state: any) => state.productConfig?.configurationOrderKey ?? null,
  (_state: any, product: any) => product,
  (_state: any, _product: any, orderKey?: string | null) => orderKey ?? null,
  (currentProduct, currentOrderKey, product, orderKey) =>
    currentProduct === product && currentOrderKey === orderKey,
);
export const selectConfigurationAdditionalData = createSelector(
  selectConfigurationContext,
  (context) => context.additionalData,
);
export const selectConfigurationAvailable = createSelector(
  selectConfigurationContext,
  (context) => context.available,
);
export const selectConfigurationOrder = createSelector(
  selectConfigurationContext,
  (context) => context.order,
);
// Preserve the legacy background lookup for ordinary configuration; only an
// explicit destination changes which cart order grants upgrades/unlocks.
const selectBackgroundOrderKey = (state: any) =>
  state.productConfig?.configurationOrderKey ?? state.additionalData?.studentId;
export const selectConfigurationUnlockedGroups = createSelector(
  (state: any) => state.v3Order?.orders,
  selectBackgroundOrderKey,
  (orders, key) => cartUtils.unlockedGroups(orders?.[key]?.cartItems || []),
);
export const selectConfigurationSingleBackgroundUpgrade = createSelector(
  (state: any) => state.v3Order?.orders,
  selectBackgroundOrderKey,
  selectConfigurationShoot,
  (orders, key, shoot) =>
    cartUtils.orderContainsSingleBackgroundUpgrade(
      orders,
      key,
      shoot?.chargeForYpxBackgroundUpgrades,
    ),
);
export const selectConfigurationPersonalizationValue = createSelector(
  selectConfigurationOrder,
  (order) => {
    for (const item of order?.cartItems || []) {
      if (item.personalizationValue) return item.personalizationValue;
      for (const child of item.children || []) {
        if (child.personalizationValue) return child.personalizationValue;
      }
    }
    return null;
  },
);
